import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import Docs from './components/Docs';
import Search from './components/Search';
import ScanLog from './components/ScanLog';
import ToolRequests from './components/ToolRequests';
import Lab from './components/Lab';
import './App.css';

axios.defaults.withCredentials = true;

interface BotStatus {
  running: boolean;
  vpnPaused: boolean;
  manualPause: boolean;
  capital: number;
  pnl: number;
  positions: number;
  trades: number;
  tokenSpend: number;
}

export default function App() {
  const [status, setStatus] = useState<BotStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'lab' | 'settings' | 'docs' | 'search' | 'scanlog' | 'tools'>(
    () => (localStorage.getItem('poly_active_tab') as any) || 'dashboard'
  );
  const [toggling, setToggling] = useState(false);
  const [xaiBalance, setXaiBalance] = useState<number | null>(null);
  const [glmUsage, setGlmUsage] = useState<any | null>(null);
  const [cryptoPrices, setCryptoPrices] = useState<Record<string, number>>({});
  const [appVersion, setAppVersion] = useState<string>('');
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelogContent, setChangelogContent] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [reqPerMin, setReqPerMin] = useState(0);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  const apiTimestamps = useRef<number[]>([]);

  const toggleBot = async () => {
    setToggling(true);
    try {
      const res = await axios.post('/api/bot/toggle');
      setStatus(prev => prev ? { ...prev, running: res.data.running, manualPause: !res.data.running } : prev);
    } catch (err) {
      console.error('Failed to toggle bot:', err);
    } finally {
      setToggling(false);
    }
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get('/api/status');
        setStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch status:', err);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, []);

  // Global API rate tracking
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      if ((config.url || '').startsWith('/api/')) {
        apiTimestamps.current.push(Date.now());
      }
      return config;
    });
    const resId = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 429) setRateLimitWarning(true);
        return Promise.reject(err);
      }
    );
    const iv = setInterval(() => {
      const now = Date.now();
      apiTimestamps.current = apiTimestamps.current.filter(t => now - t < 60000);
      setReqPerMin(apiTimestamps.current.length);
    }, 1000);
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
      clearInterval(iv);
    };
  }, []);



  useEffect(() => {
    const fetchBalance = async () => {
      // Fetch version
      try {
        const vres = await axios.get('/api/version');
        if (vres.data?.version) setAppVersion(vres.data.version);
      } catch (err) {
        console.error('Failed to fetch version:', err);
      }
      // Fetch balance
      try {
        const res = await axios.get('/api/billing/balance');
        if (res.data?.remaining != null) setXaiBalance(res.data.remaining);
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      }
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchGlm = async () => {
      try {
        const res = await axios.get('/api/zai/usage');
        setGlmUsage(res.data && !res.data.error ? res.data : null);
      } catch {
        setGlmUsage(null);
      }
    };
    fetchGlm();
    const interval = setInterval(fetchGlm, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get('/api/me', { withCredentials: true });
        if (res.data.authenticated) setIsLoggedIn(true);
      } catch {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await axios.post('/api/login', { password: passwordInput }, { withCredentials: true });
      if (res.data.success) {
        setIsLoggedIn(true);
        setPasswordInput('');
      }
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/logout', {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout failed:', err);
    }
    setIsLoggedIn(false);
    setStatus(null);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const openChangelog = async () => {
    try {
      const res = await fetch('/api/changelog');
      const text = await res.text();
      setChangelogContent(text);
    } catch (err) {
      setChangelogContent('Failed to load changelog.');
    }
    setShowChangelog(true);
  };

  const statusLabel = () => {
    if (status?.vpnPaused) return { text: 'VPN Down', cls: 'badge-vpn' };
    if (!status?.running) return { text: 'Paused', cls: 'badge-stopped' };
    return { text: 'Running', cls: 'badge-running' };
  };

  const badge = statusLabel();
  useEffect(() => {
    const PAIRS: Record<string, string> = { BTC: 'XBTUSD', ETH: 'ETHUSD', SOL: 'SOLUSD' };
    const fetchPrices = async () => {
      try {
        const results: Record<string, number> = {};
        await Promise.all(Object.entries(PAIRS).map(async ([sym, pair]) => {
          const res = await fetch(`https://api.kraken.com/0/public/Ticker?pair=${pair}`);
          const data = await res.json();
          const val = Object.values(data.result || {})[0] as any;
          if (val?.c?.[0]) results[sym] = parseFloat(val.c[0]);
        }));
        setCryptoPrices(results);
      } catch (err) {
        console.error('Failed to fetch crypto prices:', err);
      }
    };
    fetchPrices();
    const iv = setInterval(fetchPrices, 60000);
    return () => clearInterval(iv);
  }, []);

  const maxSpend = (status as any)?.maxDailyTokenSpend ?? 1.00;
  const spendPct = Math.min(100, ((status?.tokenSpend ?? 0) / maxSpend) * 100);
  const canToggle = !toggling && !status?.vpnPaused;

  if (!isLoggedIn) {
    return (
      <div className="app login-screen">
        <div className="login-box">
          <h1>🤖 Polymarket Bot</h1>
          <p className="login-subtitle">Enter password to access the dashboard</p>
          <form onSubmit={handleLogin} className="login-form">
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Password"
              disabled={loggingIn}
              autoFocus
            />
            <button type="submit" disabled={loggingIn || !passwordInput.trim()}>
              {loggingIn ? 'Logging in...' : 'Login'}
            </button>
          </form>
          {loginError && <p className="login-error">{loginError}</p>}
          <p className="login-hint">Password configured server-side via .htpasswd hash (update ADMIN_PASSWORD_HASH in .env to change)</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-top">
          <h1 className="app-title">🤖 Polymarket Bot</h1>
          <div className="header-actions">
            <span className={`status-badge ${badge.cls}`}>{badge.text}</span>
            <button
              className={`toggle-btn ${status?.running ? 'btn-pause' : 'btn-resume'}`}
              onClick={toggleBot}
              disabled={!canToggle}
              title={status?.vpnPaused ? 'Resume disabled: VPN is down' : undefined}
            >
              {status?.running ? '⏸ Pause' : '▶ Resume'}
            </button>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
            <button onClick={handleRefresh} className="refresh-btn" title="Refresh page">↻</button>
          </div>
        </div>
        <div className="header-meta">
          {appVersion && <span className="app-version" onClick={openChangelog} style={{ cursor: 'pointer' }}>v{appVersion}</span>}
          <div className="token-spend">
            <span className="spend-label">xAI tokens</span>
            <div className="spend-bar-wrap">
              <div className="spend-bar" style={{ width: `${spendPct}%`, background: spendPct > 80 ? '#ef4444' : '#6366f1' }} />
            </div>
            <span className="spend-amount">${(status?.tokenSpend ?? 0).toFixed(4)} / ${maxSpend.toFixed(2)}</span>
          </div>
          {xaiBalance != null && (
            <span className="xai-balance">⚡ ${xaiBalance.toFixed(2)} credit</span>
          )}
          {glmUsage && glmUsage.windows && glmUsage.windows[0] && (
            <span className="xai-balance" style={{ color: '#22d3ee' }} title="GLM coding-plan usage (5-hour window)">
              🎚 GLM {glmUsage.plan}
              {typeof glmUsage.windows[0].usedPct === 'number' ? ` · ${glmUsage.windows[0].usedPct}%` : ''}
              {typeof glmUsage.windows[0].resetInMs === 'number' ? ` · ${(() => { const ms = glmUsage.windows[0].resetInMs; const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000); return h > 0 ? `${h}h ${m}m` : `${m}m`; })()} left` : ''}
            </span>
          )}
          <span
            className={`rate-counter${reqPerMin > 400 ? ' rate-warn' : reqPerMin > 200 ? ' rate-caution' : ''}`}
            title="API requests in the last 60 seconds (nginx limit: 500/min)"
          >
            {reqPerMin}/min
          </span>
        </div>
        {Object.keys(cryptoPrices).length > 0 && (
          <div className="crypto-ticker">
            {Object.entries(cryptoPrices).map(([sym, price]) => (
              <span key={sym} className="ticker-item">
                {sym} <strong>${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
              </span>
            ))}
          </div>
        )}
      </header>

      <nav className="tab-nav">
        <button className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => { setActiveTab('dashboard'); localStorage.setItem('poly_active_tab', 'dashboard'); }}>Dashboard</button>
        <button className={`tab-btn ${activeTab === 'lab' ? 'active' : ''}`} onClick={() => { setActiveTab('lab'); localStorage.setItem('poly_active_tab', 'lab'); }}>🧪 Lab</button>
        <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => { setActiveTab('settings'); localStorage.setItem('poly_active_tab', 'settings'); }}>Settings</button>
        <button className={`tab-btn ${activeTab === 'docs' ? 'active' : ''}`} onClick={() => { setActiveTab('docs'); localStorage.setItem('poly_active_tab', 'docs'); }}>Docs</button>
        <button className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`} onClick={() => { setActiveTab('search'); localStorage.setItem('poly_active_tab', 'search'); }}>Search</button>
        <button className={`tab-btn ${activeTab === 'scanlog' ? 'active' : ''}`} onClick={() => { setActiveTab('scanlog'); localStorage.setItem('poly_active_tab', 'scanlog'); }}>Scan Log</button>
        <button className={`tab-btn ${activeTab === 'tools' ? 'active' : ''}`} onClick={() => { setActiveTab('tools'); localStorage.setItem('poly_active_tab', 'tools'); }}>🔧 Tools</button>
      </nav>

      <main className="main-content">
        {activeTab === 'dashboard' && <Dashboard status={status} onStatusUpdate={(update: any) => setStatus((prev: any) => prev ? { ...prev, ...update } : update)} />}
        {activeTab === 'lab' && <Lab />}
        {activeTab === 'settings' && <Settings />}
        {activeTab === 'docs' && <Docs onBack={() => setActiveTab('dashboard')} />}
        {activeTab === 'search' && <Search />}
        <div style={{ display: activeTab === 'scanlog' ? 'block' : 'none' }}>
          <ScanLog />
        </div>
        {activeTab === 'tools' && <ToolRequests />}
      </main>

      {rateLimitWarning && (
        <div className="rate-limit-banner">
          ⚠️ Rate limited (429) — nginx allows 500 req/min. Slow down or wait a moment.
          <button className="rate-limit-dismiss" onClick={() => setRateLimitWarning(false)}>✕</button>
        </div>
      )}

      {showChangelog && (
        <div className="modal-overlay" onClick={() => setShowChangelog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowChangelog(false)}>✕</button>
            <h2>Changelog</h2>
            <pre className="changelog-text">{changelogContent || 'Loading...'}</pre>
          </div>
        </div>
      )}
    </div>
  );
}